/**
 * Start a new outbound conversation (README §5 / task 7). Reuses MatchModal's
 * debounced contact-search pattern (`modalReads.searchContacts`, 300ms,
 * `tooShort` below 2 characters) rather than inventing a second one.
 *
 * Picking a search result — or opening with a prefilled contact
 * (`composePrefill.contact`, set by whatever launched this modal from a
 * ticket) — fetches that contact's real handles via `modalReads.contactHandles`
 * (chat's single email handle, plus presence) AND, separately, every labeled
 * phone number on the record via `modalReads.contactNumbers` (task 2, the
 * outbound-number-picker fix — see docs/superpowers/specs/
 * 2026-08-24-outbound-number-picker-design.md). The old `handles.sms`
 * single-handle auto-pick is GONE from this path: it guessed
 * `PHONE_FIELD_CANDIDATES.first()`, which in production was a work
 * landline — three texts, zero replies, because a landline cannot receive
 * SMS. SMS now shows every number, labeled, and the tech picks; `handles.sms`
 * itself is untouched for any other caller, just no longer read here. The
 * channel segment can only ever offer a channel this contact actually has
 * something on: chat needs a chat handle ("No email on file" otherwise), sms
 * needs at least one number on file ("No number on file" otherwise) — the
 * single available channel is pre-picked so the common case (only a phone,
 * or only an email) needs no extra click, and picking sms auto-selects the
 * first mobile-type number below it (none mobile → nothing pre-selected, the
 * tech must choose).
 *
 * `wired: false` from that lookup (Halo unreachable) is a DIFFERENT fact from
 * a contact genuinely having no handle, and must never render the same way
 * (see reads.ts's own header comment) — see `HandlesStatus` below for the
 * three-way split that keeps "no handle on file" and "could not ask Halo"
 * from ever looking identical, with a Retry affordance on the latter.
 *
 * Presence (`ContactSearchHit.presence` / `ContactHandlesResult.presence`) is
 * informational only — Global Constraints: it never disables anything here,
 * including sending to someone who is not currently online. Rendered on every search result
 * AND on the picked-contact card once one is chosen (finding 5) — the picked-contact dot is
 * the ONLY presence the deep-link-from-ticket path ever shows, since a prefilled contact never
 * passes through the search-results list at all.
 *
 * Sending calls the single `start-outbound` operation
 * (packages/shared/src/operations.ts's `StartOutboundArgs`,
 * services/ops/src/routes/operations.ts's handler). `ticketId` is
 * `z.string().min(1).optional()` — the key is omitted entirely rather than
 * sent as `''`, both for the prefilled "#<id>" chip and for the free-text
 * ticket-number input.
 */
import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import type { ThreadSummary } from '@communicator/shared'
import { Button, Modal } from '../ui'
import { ops, OpsError } from '../../lib/ops'
import { useStore } from '../../store'
import {
  modalReads,
  RECENT_TICKETS_SEAM,
  type ContactHandles,
  type ContactNumber,
  type ContactPresence,
  type ContactSearchResult,
  type DeviceSearchHit,
  type DeviceSearchResult,
  type RecentTicketsResult,
} from './reads'
import { COMPOSE_RECENT_TICKETS_UNAVAILABLE_TEXT, GENERIC_OP_ERROR } from './format'
import { formatPhoneNumber } from '../context/format'

/** "online Xm ago" only appears within this window — older than this, the console says nothing
 * rather than a stale-sounding "online 6h ago". Module const, per the brief: no config for this. */
const PRESENCE_RECENT_MS = 30 * 60_000

function presenceRecentText(presence: ContactPresence | null | undefined): string | null {
  if (!presence?.lastSeen) return null
  const seenAt = new Date(presence.lastSeen).getTime()
  if (!Number.isFinite(seenAt)) return null
  const elapsedMs = Date.now() - seenAt
  if (elapsedMs < 0 || elapsedMs > PRESENCE_RECENT_MS) return null
  return `online ${Math.round(elapsedMs / 60_000)}m ago`
}

/** Strips one leading '#' and surrounding whitespace — the same shape TicketModal's
 * `normalizeTicketInput` accepts, but returning the raw id (no '#') to match how
 * `composePrefill.ticketId` and `StartOutboundArgs.ticketId` both hold it. Empty input (nothing
 * typed) resolves to `null`, the "no ticket" case. */
function resolveTypedTicketId(raw: string): string | null {
  const trimmed = raw.trim().replace(/^#/, '').trim()
  return trimmed.length > 0 ? trimmed : null
}

/** The default shown when the handles lookup came back `wired: false` with no server-given
 * reason (an older ops build, a 404, a network error) — same fallback discipline as `reads.ts`'s
 * other unwired seams: this is a "could not ask Halo" finding, never "Halo said no". */
const HANDLES_UNREACHABLE_TEXT =
  "Could not reach Halo to look up this contact's handles. This says nothing about whether any are on file — try again, or pick someone else."

/** Same fallback discipline as `HANDLES_UNREACHABLE_TEXT` above, for the separate
 * `modalReads.contactNumbers` fetch the SMS number picker (task 2) reads from. */
const NUMBERS_UNREACHABLE_TEXT =
  "Could not reach Halo to look up this contact's numbers. This says nothing about whether any are on file — try again, or pick someone else."

interface PickedContact {
  contactId: string
  name: string
}

interface PickedDevice {
  deviceId: string
  hostname: string
  presence: ContactPresence | null
}

/** Which target kind the composer is offering right now — task 7 (device-targets). 'people' is
 * today's flow, byte-for-byte; 'machines' is the new one. Never both at once: switching clears the
 * OTHER mode's own selection/search (see `switchMode` below) so a stale contact or device can
 * never survive a flip back. */
type ComposeMode = 'people' | 'machines'

/**
 * `'none'` — no contact picked yet, nothing to show.
 * `'loading'` — the fetch is in flight; both channel buttons are disabled with no reason text
 *   (fix for finding 2: they used to flash "No mobile/email on file" for the same tick a
 *   genuinely handle-less contact would show, which is exactly the two different facts this
 *   whole file's read seams exist to keep apart).
 * `'wired'` — Halo answered; `handles` holds the real (possibly null-per-field) answer.
 * `'unwired'` — Halo could not be asked; `handlesReason` holds why. Rendered as its own inline
 *   notice (fix for finding 1) rather than the false "No X on file" labels, which claimed a
 *   contact fact Halo never actually reported.
 */
type HandlesStatus = 'none' | 'loading' | 'wired' | 'unwired'

export function ComposeModal() {
  const modal = useStore((s) => s.modal)
  const composePrefill = useStore((s) => s.composePrefill)
  const closeModal = useStore((s) => s.closeModal)
  const selectThread = useStore((s) => s.selectThread)

  const open = modal === 'compose'

  const [mode, setMode] = useState<ComposeMode>('people')

  const [query, setQuery] = useState('')
  const [search, setSearch] = useState<ContactSearchResult>({ wired: true, results: [], tooShort: true })
  const [searching, setSearching] = useState(false)

  const [contact, setContact] = useState<PickedContact | null>(null)
  const [handles, setHandles] = useState<ContactHandles | null>(null)
  const [handlesStatus, setHandlesStatus] = useState<HandlesStatus>('none')
  const [handlesReason, setHandlesReason] = useState<string | null>(null)
  const [presence, setPresence] = useState<ContactPresence | null>(null)
  const [channel, setChannel] = useState<'sms' | 'chat' | null>(null)

  // The outbound number picker (task 2): every labeled phone number on the picked contact,
  // fetched separately from `handles` above (its own `modalReads.contactNumbers` seam, its own
  // `wired`/loading/unwired lifecycle — same `HandlesStatus` shape, reused rather than duplicated
  // since the three-way split means the same thing here: "Halo answered" vs "we could not ask").
  // `selectedNumberField` holds the CHOSEN row's `field` (a radio's identity, not its formatted
  // display value) — `null` means nothing is selected, which is the landline-only default (see
  // the picker-default effect below) and which keeps Send disabled until the tech picks one.
  const [numbers, setNumbers] = useState<ContactNumber[]>([])
  const [numbersStatus, setNumbersStatus] = useState<HandlesStatus>('none')
  const [numbersReason, setNumbersReason] = useState<string | null>(null)
  const [selectedNumberField, setSelectedNumberField] = useState<string | null>(null)

  const [ticketId, setTicketId] = useState<string | null>(null)
  const [ticketInput, setTicketInput] = useState('')

  // Recent tickets (task 2): the picked contact's own tickets, offered as clickable rows between
  // the contact card and the typed ticket input. `selectedRecentTicketId` is purely a paint flag
  // for which row (if any) supplied `ticketInput`'s current value — clicking a row and typing
  // both write the SAME `ticketInput` field, so there is exactly one source of truth for what
  // gets attached at send time (see the module doc comment).
  const [recentTickets, setRecentTickets] = useState<RecentTicketsResult>(RECENT_TICKETS_SEAM)
  const [selectedRecentTicketId, setSelectedRecentTicketId] = useState<string | null>(null)

  // What this component last ACTUALLY APPLIED to local state from `composePrefill`, tracked
  // per field so the ownership guard below can tell "the store moved on" apart from "the tech
  // typed/picked something themselves" — see the combined sync effect a bit further down.
  // `undefined` means "nothing applied yet this open" (distinct from `null`, a real "no
  // ticket"/"no contact" value that WAS applied). Two independent refs, not one combined
  // object, because a tech can diverge on the ticket while the prefilled contact is still
  // trusted, or vice versa — and each ref is written ONLY on the branch that actually applied
  // its value, never on the branch that skipped it, so it never claims ownership of a value
  // that never reached local state.
  const lastAppliedTicketIdRef = useRef<string | null | undefined>(undefined)
  const lastAppliedContactIdRef = useRef<string | null | undefined>(undefined)

  // Staleness guard for `pickContact`'s two fetches (handles, recent tickets — code review
  // finding, 2026-08-23): pick A (slow) then pick B (fast) races A's response landing AFTER B's
  // and overwriting B's freshly-rendered card/list with A's stale data. Set to the contact id
  // `pickContact` is CURRENTLY fetching for; each resolve checks it still matches before calling
  // any setState. One ref covers both fetches since they are both kicked off from the same
  // `pickContact` invocation for the same contact — same idiom as the debounced search effect's
  // `cancelled` closure flag a few lines up, just as a ref instead of a closure because these two
  // fetches outlive their own effect (they are fired from an event handler, not `useEffect`).
  const latestPickedContactIdRef = useRef<string | null>(null)

  // Machines (task 7, device-targets). Deliberately its own small state block, not folded into
  // the contact fields above — a device has no handles, no channel choice (chat-only, spec's own
  // "a machine has exactly one way to hear you"), and no recent-tickets list (v1: that read keys
  // off a contact, and a device has none — noted in the PR per the brief). `deviceSearch` mirrors
  // `search`'s own shape/discipline (reads.ts's `wired` rule) but has no `tooShort` field on the
  // wire — same as contacts, "too short" is a pure client-side gate on running the debounced
  // fetch at all (see the effect below), never something the server is asked to answer.
  const [deviceQuery, setDeviceQuery] = useState('')
  const [deviceSearch, setDeviceSearch] = useState<DeviceSearchResult>({ wired: true, devices: [] })
  const [deviceSearching, setDeviceSearching] = useState(false)
  const [pickedDevice, setPickedDevice] = useState<PickedDevice | null>(null)

  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Set only when `start-outbound` answers `alreadyOpen: true` — an SMS handle, or (task 7) a
  // device, that already has an open thread. Distinct from `error`: this is not a failure, it is
  // the operation's own successful (200) answer to "start a conversation", so it gets its own
  // notice rather than routing through the error banner. `kind` picks the copy — the device
  // notice never names the machine (brief's exact text: "There's already an open conversation
  // with this machine."), while the contact notice still names the person, byte-for-byte
  // unchanged from before this task. Cleared on a fresh send attempt or on modal close.
  const [alreadyOpenThread, setAlreadyOpenThread] = useState<{ id: string; kind: 'contact' | 'device'; name: string } | null>(
    null,
  )

  // Debounced contact search — identical timing/shape to MatchModal's "Someone else?" search,
  // reused rather than reinvented.
  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) {
      setSearch({ wired: true, results: [], tooShort: true })
      setSearching(false)
      return
    }
    setSearching(true)
    let cancelled = false
    const timer = setTimeout(() => {
      modalReads
        .searchContacts(q)
        .then((r) => {
          if (!cancelled) setSearch(r)
        })
        .catch(() => {
          if (!cancelled) {
            setSearch({ wired: false, results: [], reason: 'The contact search could not be run.' })
          }
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, query])

  // Debounced device search — same 300ms/2-character-minimum idiom as the contact search effect
  // above, mirrored rather than shared because the two feed different state and different modal
  // reads seams (task 7's own `modalReads.searchDevices`). Gated on `mode === 'machines'` so
  // switching back to People never leaves this timer running against a query nobody is looking
  // at.
  useEffect(() => {
    if (!open || mode !== 'machines') return
    const q = deviceQuery.trim()
    if (q.length < 2) {
      setDeviceSearch({ wired: true, devices: [] })
      setDeviceSearching(false)
      return
    }
    setDeviceSearching(true)
    let cancelled = false
    const timer = setTimeout(() => {
      modalReads
        .searchDevices(q)
        .then((r) => {
          if (!cancelled) setDeviceSearch(r)
        })
        .catch(() => {
          if (!cancelled) {
            setDeviceSearch({ wired: false, devices: [], reason: 'The device search could not be run.' })
          }
        })
        .finally(() => {
          if (!cancelled) setDeviceSearching(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, mode, deviceQuery])

  /**
   * Switches between People and Machines, clearing whatever the OTHER mode had selected/searched
   * — the brief's own guard: "no stale contact when you flip to machines and back." Ticket
   * (`ticketId`/`ticketInput`) and the typed `body` are deliberately NOT touched here: both are
   * facts about THIS send, not about which target kind is picking the destination, so a tech who
   * flips the toggle mid-compose does not lose a ticket number or a half-typed message.
   */
  function switchMode(next: ComposeMode) {
    if (next === mode) return
    // Review fix (Minor 1): an `alreadyOpen` banner is a fact about the mode that produced it
    // (its copy, its target name, its Open-conversation thread id) — flipping modes while it's
    // showing must not leave it floating stale above the OTHER mode's now-unrelated UI.
    setAlreadyOpenThread(null)
    if (next === 'machines') {
      setContact(null)
      setQuery('')
      setSearch({ wired: true, results: [], tooShort: true })
      setHandles(null)
      setHandlesStatus('none')
      setHandlesReason(null)
      setPresence(null)
      setChannel(null)
      setNumbers([])
      setNumbersStatus('none')
      setNumbersReason(null)
      setSelectedNumberField(null)
      setRecentTickets(RECENT_TICKETS_SEAM)
      setSelectedRecentTicketId(null)
      latestPickedContactIdRef.current = null
    } else {
      setPickedDevice(null)
      setDeviceQuery('')
      setDeviceSearch({ wired: true, devices: [] })
      setDeviceSearching(false)
    }
    setMode(next)
  }

  function pickDevice(hit: DeviceSearchHit) {
    setPickedDevice({ deviceId: hit.deviceId, hostname: hit.hostname, presence: hit.presence })
  }

  async function pickContact(picked: PickedContact) {
    // Claims this pick as the current one BEFORE either fetch goes out — both resolves below
    // check this still matches their own contact id, so a slow response for a contact the tech
    // has since moved on from is dropped on the floor instead of overwriting the current card.
    latestPickedContactIdRef.current = picked.contactId
    setContact(picked)
    setChannel(null)
    setHandles(null)
    setHandlesReason(null)
    setPresence(null)
    setHandlesStatus('loading')
    // The number picker's own fetch (task 2) — cleared and re-fetched in lockstep with `handles`
    // above, on the SAME staleness guard (`latestPickedContactIdRef`, checked once below after
    // both fetches land), because both are "what can I send this NEW contact" facts a stale
    // response for the abandoned contact must never overwrite.
    setNumbers([])
    setNumbersReason(null)
    setSelectedNumberField(null)
    setNumbersStatus('loading')
    // Recent tickets (task 2): the selected row is cleared before the refetch, per the brief's
    // own rule — a row picked for the PREVIOUS contact must never carry into the new contact's
    // ticket input. Only clears `ticketInput` itself when a row actually supplied its current
    // value (`selectedRecentTicketId !== null`) — a ticket number the tech typed by hand is a
    // choice about THIS send, not about the old contact, so it survives a contact change same as
    // it always has. Fired independently of the handles fetch below (not awaited together) so a
    // slow or unwired recent-tickets lookup can never delay the channel picker, which is the
    // thing every other test in this file actually waits on.
    if (selectedRecentTicketId !== null) setTicketInput('')
    setSelectedRecentTicketId(null)
    setRecentTickets(RECENT_TICKETS_SEAM)
    void modalReads.contactRecentTickets(picked.contactId).then((r) => {
      if (latestPickedContactIdRef.current === picked.contactId) setRecentTickets(r)
    })
    // Handles (chat + presence) and numbers (sms) are fetched together — two independent Halo
    // reads, not one waiting on the other — and checked against the staleness guard exactly once,
    // after both land, so a contact switched away from mid-flight drops BOTH stale answers on the
    // floor rather than only one.
    const [handlesResult, numbersResult] = await Promise.all([
      modalReads.contactHandles(picked.contactId),
      modalReads.contactNumbers(picked.contactId),
    ])
    if (latestPickedContactIdRef.current !== picked.contactId) return

    if (handlesResult.wired !== true) {
      // "We could not ask Halo" — never rendered as "Halo said no handles on file", per
      // reads.ts's own rule. `handles`/`presence` stay null; chat stays disabled, but with the
      // real reason, not a fabricated one.
      setHandlesStatus('unwired')
      setHandlesReason(handlesResult.reason ?? HANDLES_UNREACHABLE_TEXT)
    } else {
      setHandlesStatus('wired')
      setHandles(handlesResult.handles)
      setPresence(handlesResult.presence)
    }

    if (numbersResult.wired !== true) {
      setNumbersStatus('unwired')
      setNumbersReason(numbersResult.reason ?? NUMBERS_UNREACHABLE_TEXT)
    } else {
      setNumbersStatus('wired')
      setNumbers(numbersResult.numbers)
      // Pre-select the first mobile-type number (the common case, spec's own "fast path"); none
      // mobile leaves nothing selected — the tech must pick, rather than the composer guessing a
      // landline the way the auto-pick bug used to.
      const firstMobile = numbersResult.numbers.find((n) => n.isMobile)
      setSelectedNumberField(firstMobile ? firstMobile.field : null)
    }
  }

  // Auto-select the single available channel — the common case (a contact with only a phone, or
  // only an email) needs no extra click. Leaves the choice to the dispatcher when both (or
  // neither) are on file. `smsOk`/`chatOk` come from TWO independent fetches now (task 2: sms
  // availability is "does this contact have any number at all", from `numbers`/`numbersStatus` —
  // no longer `handles.sms`, whose single-guess auto-pick was the landline bug); both default to
  // false while their own fetch is loading/unwired/none, so this effect is already a no-op until
  // whichever one settles.
  useEffect(() => {
    const smsOk = numbersStatus === 'wired' && numbers.length > 0
    const chatOk = handlesStatus === 'wired' && handles !== null && handles.chat !== null
    if (smsOk && !chatOk) setChannel('sms')
    else if (chatOk && !smsOk) setChannel('chat')
  }, [handlesStatus, handles, numbersStatus, numbers])

  /**
   * Sync `composePrefill` (ticket + contact) into local state. Re-keyed on `composePrefill`
   * itself, NOT just `open` — a deep link (Task 8's `useComposeFromLink`) opens the composer
   * TWICE while `open` stays true→true the whole time: an immediate `{ ticketId, contact: null
   * }`, then an async upgrade to `{ ticketId, contact }` once `modalReads.composePrefill`
   * answers. Keying on `open` alone (the original bug: review finding, Critical #1) meant that
   * second revision's effect never re-ran at all, so the fetched contact reached the STORE but
   * never the rendered composer — the tech would still see the plain search box with the
   * ticket's own contact never shown, on the single most common path through this modal.
   *
   * Re-running on every `composePrefill` change is not enough by itself, though: a tech can
   * search-pick their own contact, or clear the ticket chip, in the gap before that async
   * upgrade lands (before pickContact resolves it's the ONLY thing shown, so this window is
   * real, not theoretical). `lastAppliedTicketIdRef`/`lastAppliedContactIdRef` are the ownership
   * guard — each only applies a new prefill value when local state still matches what THIS
   * effect last actually wrote there; once local state has diverged (a manual pick, a manual
   * clear), the prefill upgrade for that field is dropped on the floor for the rest of this open
   * session rather than clobbering the tech's own choice. Each ref is updated ONLY inside the
   * branch that applies its value — never on the branch that was skipped — so a skipped sync can
   * never be mistaken for one that landed.
   */
  useEffect(() => {
    if (!open) {
      lastAppliedTicketIdRef.current = undefined
      lastAppliedContactIdRef.current = undefined
      return
    }
    const prefillTicketId = composePrefill?.ticketId ?? null
    const prefillContact = composePrefill?.contact ?? null

    const ticketStillOurs =
      lastAppliedTicketIdRef.current === undefined || ticketId === lastAppliedTicketIdRef.current
    if (ticketStillOurs) {
      if (ticketId !== prefillTicketId) setTicketId(prefillTicketId)
      lastAppliedTicketIdRef.current = prefillTicketId
    }

    const contactStillOurs =
      lastAppliedContactIdRef.current === undefined
        ? contact === null
        : (contact?.contactId ?? null) === lastAppliedContactIdRef.current
    if (contactStillOurs) {
      if (prefillContact && prefillContact.contactId !== contact?.contactId) {
        void pickContact(prefillContact)
      }
      lastAppliedContactIdRef.current = prefillContact?.contactId ?? null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, composePrefill])

  // A fresh modal starts fresh — reopening it must not show the last person somebody composed to.
  useEffect(() => {
    if (!open) {
      setMode('people')
      setQuery('')
      setSearch({ wired: true, results: [], tooShort: true })
      setContact(null)
      setHandles(null)
      setHandlesStatus('none')
      setHandlesReason(null)
      setPresence(null)
      setChannel(null)
      setNumbers([])
      setNumbersStatus('none')
      setNumbersReason(null)
      setSelectedNumberField(null)
      setTicketInput('')
      setBody('')
      setError(null)
      setBusy(false)
      setAlreadyOpenThread(null)
      setRecentTickets(RECENT_TICKETS_SEAM)
      setSelectedRecentTicketId(null)
      latestPickedContactIdRef.current = null
      setDeviceQuery('')
      setDeviceSearch({ wired: true, devices: [] })
      setDeviceSearching(false)
      setPickedDevice(null)
    }
  }, [open])

  if (!open) return null

  // The number picker's own selection (task 2) — the SELECTED row's `value`, never `handles.sms`
  // (that single-guess auto-pick is gone from this path; see the module doc comment). `null` when
  // nothing is selected, which is the honest state for a landline-only contact until the tech
  // picks a row, and which is exactly what keeps Send disabled in that case below.
  const selectedNumber = numbers.find((n) => n.field === selectedNumberField) ?? null
  const currentHandle =
    channel === 'sms' ? (selectedNumber?.value ?? null) : channel === 'chat' ? (handles?.chat ?? null) : null
  const canSend =
    mode === 'people'
      ? contact !== null && channel !== null && currentHandle !== null && body.trim().length > 0 && !busy
      : pickedDevice !== null && body.trim().length > 0 && !busy

  type StartOutboundResult = { thread: ThreadSummary; reused: boolean } | { thread: ThreadSummary; alreadyOpen: true }

  async function handleSendToPerson() {
    if (!contact || !channel || !currentHandle) return
    const trimmedBody = body.trim()
    if (!trimmedBody) return
    setBusy(true)
    setError(null)
    setAlreadyOpenThread(null)
    try {
      const resolvedTicketId = ticketId ?? resolveTypedTicketId(ticketInput)
      const result = (await ops.call(
        'start-outbound',
        resolvedTicketId
          ? {
              channel,
              haloContactId: contact.contactId,
              handle: currentHandle,
              ticketId: resolvedTicketId,
              body: trimmedBody,
            }
          : {
              channel,
              haloContactId: contact.contactId,
              handle: currentHandle,
              body: trimmedBody,
            },
      )) as StartOutboundResult

      // SMS never silently appends (Andrew's call, 2026-08-23; services/ops's
      // `start-outbound` handler, operations.ts). Nothing was sent, nothing was
      // appended, no ticket was linked and no claim was touched — the operation did
      // exactly one thing, the lookup, and answered with the thread that is already
      // open. So this is not an error: the modal stays open, the typed body stays in
      // the textarea (the tech may want to copy it into the existing thread instead),
      // and an inline notice offers the one useful next step.
      if ('alreadyOpen' in result && result.alreadyOpen) {
        setAlreadyOpenThread({ id: result.thread.id, kind: 'contact', name: contact.name })
        return
      }

      // Every remaining outcome is a create: chat never reuses an existing thread at all
      // (the client chat app is built for multiple concurrent threads per contact — Andrew,
      // 2026-08-23), and sms either created fresh or already returned above via
      // `alreadyOpen`. So `result.thread` here is always a brand-new thread this tech owns
      // — `reused` stays on the wire shape for now but nothing in the console branches on
      // it any more.
      selectThread(result.thread.id)
      closeModal()
    } catch (e) {
      setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Machines (task 7). Same shape and the same `alreadyOpen`/create split as the person path
   * above — `start-outbound` answers device sends identically (design doc: "same shape as SMS")
   * — just with `deviceId` in place of `haloContactId`/`handle`, and `channel` always `'chat'`
   * (a machine has no phone number, spec's own "Chat only"), never read off the (hidden, for
   * machines) channel picker state.
   */
  async function handleSendToDevice() {
    if (!pickedDevice) return
    const trimmedBody = body.trim()
    if (!trimmedBody) return
    setBusy(true)
    setError(null)
    setAlreadyOpenThread(null)
    try {
      const resolvedTicketId = ticketId ?? resolveTypedTicketId(ticketInput)
      const result = (await ops.call(
        'start-outbound',
        resolvedTicketId
          ? { channel: 'chat', deviceId: pickedDevice.deviceId, ticketId: resolvedTicketId, body: trimmedBody }
          : { channel: 'chat', deviceId: pickedDevice.deviceId, body: trimmedBody },
      )) as StartOutboundResult

      if ('alreadyOpen' in result && result.alreadyOpen) {
        setAlreadyOpenThread({ id: result.thread.id, kind: 'device', name: pickedDevice.hostname })
        return
      }

      selectThread(result.thread.id)
      closeModal()
    } catch (e) {
      setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
    } finally {
      setBusy(false)
    }
  }

  async function handleSend() {
    if (mode === 'people') {
      await handleSendToPerson()
    } else {
      await handleSendToDevice()
    }
  }

  function handleTicketKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') e.preventDefault()
  }

  // Recent tickets (task 2): a row and the typed input resolve to the SAME `ticketInput` field —
  // clicking a row fills it exactly as if typed, so `handleSend`'s existing
  // `resolveTypedTicketId(ticketInput)` needs no new branch. Clicking the already-selected row
  // deselects (clears the input); clicking a different row simply overwrites both.
  function handleRecentTicketClick(ticket: string) {
    if (selectedRecentTicketId === ticket) {
      setSelectedRecentTicketId(null)
      setTicketInput('')
    } else {
      setSelectedRecentTicketId(ticket)
      setTicketInput(ticket)
    }
  }

  // "No prefill chip" (brief's own wording) means the ticket CHIP state (`ticketId`, set only by
  // composePrefill or cleared by the × button) is null — not merely "no ticket text typed yet".
  // A row-selected ticketInput never sets `ticketId`, so picking a row never itself hides this
  // list; only a genuine prefill chip does.
  // Machines (task 7): the recent-tickets list keys off a contact, and a device has none — v1
  // hides it outright for machines rather than showing an always-empty list (noted in the PR per
  // the brief). `mode === 'people'` is the explicit gate rather than relying on `contact` being
  // null in machines mode as a side effect of `switchMode` — this stays correct even if that
  // invariant is ever broken elsewhere.
  const showRecentTickets =
    mode === 'people' &&
    contact !== null &&
    ticketId === null &&
    recentTickets.wired === true &&
    recentTickets.tickets.length > 0
  const showRecentTicketsUnavailable =
    mode === 'people' &&
    contact !== null &&
    ticketId === null &&
    recentTickets.wired === false &&
    (recentTickets.reason === 'halo-unreachable' || recentTickets.reason === 'not-configured')

  return (
    <Modal open={open} title="New conversation" onClose={closeModal}>
      {/* People | Machines (task 7, device-targets). Built with the same two-`Button` idiom as
          the "Send by" channel segment further down (SMS | Chat) rather than a new segmented
          control — same visual language, one less component. People is today's flow, untouched;
          Machines targets a Tossker-enrolled device instead of a Halo contact. */}
      <div className="mb-3 flex gap-2">
        <Button
          data-testid="compose-mode-people"
          variant={mode === 'people' ? 'primary' : 'secondary'}
          onClick={() => switchMode('people')}
        >
          People
        </Button>
        <Button
          data-testid="compose-mode-machines"
          variant={mode === 'machines' ? 'primary' : 'secondary'}
          onClick={() => switchMode('machines')}
        >
          Machines
        </Button>
      </div>

      {error && (
        <p
          data-testid="compose-modal-error"
          role="alert"
          className="mb-3 rounded-[8px] bg-bad-subtle px-3 py-2 text-[12px] text-bad"
        >
          {error}
        </p>
      )}

      {/* Finding 4: the deep-link prefetch's own honest failure (`composePrefill.error`, set by
          ConsolePage's `useComposeFromLink`) — distinct from `error` above, which is a failed
          SEND. Both can be true at once (a bad deep link, then a failed send on whatever the
          tech typed instead), so both render, never one replacing the other. */}
      {composePrefill?.error && (
        <p
          data-testid="compose-prefill-error"
          role="alert"
          className="mb-3 rounded-[8px] bg-warn-subtle px-3 py-2 text-[12px] text-warn"
        >
          {composePrefill.error}
        </p>
      )}

      {/* SMS "already open" answer (Andrew's call, 2026-08-23): not a failed send — the
          operation succeeded and its answer is "one is already open" — so this is its own
          notice, not routed through `compose-modal-error` above. The modal stays open and
          the typed body is left exactly where it is. */}
      {alreadyOpenThread && (
        <div
          data-testid="compose-already-open-notice"
          role="alert"
          className="mb-3 flex items-center justify-between gap-3 rounded-[8px] bg-warn-subtle px-3 py-2 text-[12px] text-warn"
        >
          <span>
            {alreadyOpenThread.kind === 'device'
              ? "There's already an open conversation with this machine."
              : `There's already an open text conversation with ${alreadyOpenThread.name}.`}
          </span>
          <Button
            data-testid="compose-already-open-btn"
            variant="secondary"
            onClick={() => {
              selectThread(alreadyOpenThread.id)
              closeModal()
            }}
          >
            Open conversation
          </Button>
        </div>
      )}

      {mode === 'people' &&
        (contact ? (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-[8px] border border-subtle bg-card px-2.5 py-2">
            <div className="flex min-w-0 items-center gap-2">
              {/* Finding 5: the same presence idiom the search results show (below) — this picked-
                  contact card is the ONLY presence the deep-link path ever renders, since a
                  prefilled contact never passes through the search-results list at all. */}
              <span
                data-testid="compose-picked-contact-presence"
                aria-label={presence?.live === true ? 'Active' : 'Away'}
                className={
                  presence?.live === true
                    ? 'h-[7px] w-[7px] flex-none rounded-full bg-ok'
                    : 'h-[7px] w-[7px] flex-none rounded-full border border-subtle bg-transparent'
                }
              />
              <div className="min-w-0">
                <span data-testid="compose-picked-contact" className="truncate text-[13px] font-medium text-hi">
                  {contact.name}
                </span>
                {presenceRecentText(presence) && (
                  <div data-testid="compose-picked-contact-recent" className="text-[11px] text-lo">
                    {presenceRecentText(presence)}
                  </div>
                )}
              </div>
            </div>
            <Button
              data-testid="compose-change-contact-btn"
              variant="secondary"
              onClick={() => {
                setContact(null)
                setHandles(null)
                setHandlesStatus('none')
                setHandlesReason(null)
                setPresence(null)
                setChannel(null)
                setNumbers([])
                setNumbersStatus('none')
                setNumbersReason(null)
                setSelectedNumberField(null)
                setQuery('')
                // Code review finding 2026-08-24 (M3): the staleness guard for `pickContact`'s two
                // fetches is this same ref — left pointed at the abandoned contact, a slow
                // in-flight `contactHandles`/`contactRecentTickets` response for THAT contact still
                // matches it and writes into state while `contact` is null. Invisible today (both
                // setters land on now-irrelevant state), a trap the next time something renders off
                // `handlesStatus` without also checking `contact !== null`.
                latestPickedContactIdRef.current = null
                // Deliberately NOT resetting `recentTickets`/`selectedRecentTicketId`/`ticketInput`
                // here — the list is already hidden the moment `contact` goes null
                // (`showRecentTickets` requires `contact !== null`), and the next real pick's
                // `pickContact` is the single place that clears a row selection "before refetch"
                // per the brief. Clearing it here too would zero `selectedRecentTicketId` a render
                // early, which would make `pickContact`'s own "was this ticketInput row-derived"
                // check see a false negative and leave the OLD contact's ticket number sitting in
                // the input for the new contact.
              }}
            >
              Change
            </Button>
          </div>
        ) : (
          <div className="mb-3">
            <label htmlFor="compose-search" className="mb-1.5 block text-[12px] font-medium text-hi">
              Who is this to?
            </label>
            <input
              id="compose-search"
              data-testid="compose-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search contacts by name or email…"
              className="w-full rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-[13px] text-hi placeholder:text-lo"
            />

            {searching ? (
              <p className="mt-1.5 text-[12px] text-lo">Searching…</p>
            ) : !search.wired ? (
              <p role="alert" data-testid="compose-search-error" className="mt-1.5 text-[12px] text-warn">
                {search.reason}
              </p>
            ) : search.tooShort ? null : search.results.length === 0 ? (
              <p data-testid="compose-search-empty" className="mt-1.5 text-[12px] text-lo">
                No contact matches “{query.trim()}”.
              </p>
            ) : (
              <div data-testid="compose-search-results" className="mt-1.5">
                {search.results.map((r) => {
                  const recent = presenceRecentText(r.presence)
                  const live = r.presence?.live === true
                  return (
                    <div
                      key={r.contactId}
                      data-testid={`compose-search-result-${r.contactId}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => void pickContact(r)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          void pickContact(r)
                        }
                      }}
                      className="mb-1.5 flex cursor-pointer items-center gap-2 rounded-[8px] border border-subtle bg-card px-2.5 py-2"
                    >
                      <span
                        aria-label={live ? 'Active' : 'Away'}
                        className={
                          live
                            ? 'h-[7px] w-[7px] flex-none rounded-full bg-ok'
                            : 'h-[7px] w-[7px] flex-none rounded-full border border-subtle bg-transparent'
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] text-hi">
                          {r.name}
                          {r.company ? <span className="text-mid"> · {r.company}</span> : null}
                        </div>
                        {recent && <div className="text-[11px] text-lo">{recent}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))}

      {/* Machines (task 7). Same shape as the People block above — a picked-target card, or a
          search box with a debounced results list — because a device is just a different kind of
          target, not a different composer. No company/handle text (a device has neither); the
          SAME presence-dot idiom (Active/Away aria-labels, filled vs hollow) as every other
          presence indicator in this modal. */}
      {mode === 'machines' &&
        (pickedDevice ? (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-[8px] border border-subtle bg-card px-2.5 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <span
                data-testid="compose-picked-device-presence"
                aria-label={pickedDevice.presence?.live === true ? 'Active' : 'Away'}
                className={
                  pickedDevice.presence?.live === true
                    ? 'h-[7px] w-[7px] flex-none rounded-full bg-ok'
                    : 'h-[7px] w-[7px] flex-none rounded-full border border-subtle bg-transparent'
                }
              />
              <div className="min-w-0">
                <span data-testid="compose-picked-device" className="truncate text-[13px] font-medium text-hi">
                  {pickedDevice.hostname}
                </span>
                {presenceRecentText(pickedDevice.presence) && (
                  <div data-testid="compose-picked-device-recent" className="text-[11px] text-lo">
                    {presenceRecentText(pickedDevice.presence)}
                  </div>
                )}
              </div>
            </div>
            <Button
              data-testid="compose-change-device-btn"
              variant="secondary"
              onClick={() => {
                setPickedDevice(null)
                setDeviceQuery('')
              }}
            >
              Change
            </Button>
          </div>
        ) : (
          <div className="mb-3">
            <label htmlFor="compose-device-search" className="mb-1.5 block text-[12px] font-medium text-hi">
              Which machine?
            </label>
            <input
              id="compose-device-search"
              data-testid="compose-device-search"
              type="text"
              value={deviceQuery}
              onChange={(e) => setDeviceQuery(e.target.value)}
              placeholder="Search machines by hostname…"
              className="w-full rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-[13px] text-hi placeholder:text-lo"
            />

            {deviceSearching ? (
              <p className="mt-1.5 text-[12px] text-lo">Searching…</p>
            ) : !deviceSearch.wired ? (
              <p role="alert" data-testid="compose-device-search-error" className="mt-1.5 text-[12px] text-warn">
                {deviceSearch.reason}
              </p>
            ) : deviceQuery.trim().length < 2 ? null : deviceSearch.devices.length === 0 ? (
              <p data-testid="compose-device-search-empty" className="mt-1.5 text-[12px] text-lo">
                No machine matches “{deviceQuery.trim()}”.
              </p>
            ) : (
              <div data-testid="compose-device-search-results" className="mt-1.5">
                {deviceSearch.devices.map((d) => {
                  const recent = presenceRecentText(d.presence)
                  const live = d.presence?.live === true
                  return (
                    <div
                      key={d.deviceId}
                      data-testid={`compose-device-search-result-${d.deviceId}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => pickDevice(d)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          pickDevice(d)
                        }
                      }}
                      className="mb-1.5 flex cursor-pointer items-center gap-2 rounded-[8px] border border-subtle bg-card px-2.5 py-2"
                    >
                      <span
                        aria-label={live ? 'Active' : 'Away'}
                        className={
                          live
                            ? 'h-[7px] w-[7px] flex-none rounded-full bg-ok'
                            : 'h-[7px] w-[7px] flex-none rounded-full border border-subtle bg-transparent'
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] text-hi">
                          {d.hostname}
                          {d.client ? ` · ${d.client}` : ''}
                        </div>
                        {recent && <div className="text-[11px] text-lo">{recent}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))}

      {/* The channel segment is People-only (task 7): a machine has exactly one way to hear you
          (chat — spec's own "Chat only" wording), so there is no choice to offer. `handleSend`
          hard-codes `channel: 'chat'` on the device path regardless of whatever this section last
          left `channel` set to. */}
      {mode === 'people' && (
        <div className="mb-3">
          <p className="mb-1.5 text-[12px] font-medium text-hi">Send by</p>
          <div className="flex gap-2">
            <div>
              {/* Task 2: sms availability is "does this contact have ANY number on file" —
                  `numbersStatus`/`numbers`, not `handles.sms` (that single-guess auto-pick, which
                  is what texted a landline in production, is gone from this component). */}
              <Button
                data-testid="compose-channel-sms"
                variant={channel === 'sms' ? 'primary' : 'secondary'}
                disabled={numbersStatus !== 'wired' || numbers.length === 0}
                onClick={() => setChannel('sms')}
              >
                SMS
              </Button>
              {numbersStatus === 'wired' && numbers.length === 0 && (
                <p data-testid="compose-channel-sms-reason" className="mt-1 text-[11px] text-lo">
                  No number on file
                </p>
              )}
            </div>
            <div>
              <Button
                data-testid="compose-channel-chat"
                variant={channel === 'chat' ? 'primary' : 'secondary'}
                disabled={handlesStatus !== 'wired' || !handles?.chat}
                onClick={() => setChannel('chat')}
              >
                Chat
              </Button>
              {handlesStatus === 'wired' && !handles?.chat && (
                <p data-testid="compose-channel-chat-reason" className="mt-1 text-[11px] text-lo">
                  No email on file
                </p>
              )}
            </div>
          </div>
          {/* Fix (finding 2): a neutral loading state between picking a contact and either fetch
              resolving — no reason text at all, rather than the false "No X on file" both buttons
              showed for that one tick regardless of what Halo would actually answer. Two
              independent fetches now (task 2: handles, numbers), so this covers either still
              being in flight. */}
          {(handlesStatus === 'loading' || numbersStatus === 'loading') && (
            <p data-testid="compose-channel-loading" className="mt-1.5 text-[12px] text-lo">
              Looking up this contact's handles…
            </p>
          )}
          {/* Fix (finding 1): "Halo said no" and "we could not ask Halo" must never render the
              same way (reads.ts's own rule) — this is the second one, with a retry path, instead
              of the false "No mobile/email/number on file" labels a failed lookup used to
              produce. Either the handles fetch or the numbers fetch (task 2) failing surfaces
              this same notice — in practice both are the same Halo contact lookup, so they fail
              together — and Retry re-runs `pickContact`, which re-fires both. */}
          {(handlesStatus === 'unwired' || numbersStatus === 'unwired') && (
            <div className="mt-1.5 flex items-center gap-2">
              <p role="alert" data-testid="compose-channel-unreachable" className="text-[12px] text-warn">
                {handlesStatus === 'unwired' ? handlesReason : numbersReason}
              </p>
              <Button
                data-testid="compose-channel-retry-btn"
                variant="secondary"
                onClick={() => contact && void pickContact(contact)}
              >
                Retry
              </Button>
            </div>
          )}
          {/* The outbound number picker (task 2): only once sms is the ACTIVE channel and the
              contact genuinely has numbers — per the design doc's "When a contact is picked and
              the SMS channel is active." Every number is listed, labeled, per "the tech sees the
              full picture and avoids landlines themselves" — never filtered down to "just the
              mobiles," which would silently hide the very information this feature exists to
              surface. `formatPhoneNumber` (../context/format.ts) is the SAME formatter the
              context pane's SMS row already uses — one phone-formatting rule in this app, not
              two that could drift apart. */}
          {mode === 'people' && channel === 'sms' && numbersStatus === 'wired' && numbers.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-[11px] font-semibold uppercase text-lo">Text which number?</p>
              <div data-testid="compose-number-picker" role="radiogroup" aria-label="Text which number?">
                {numbers.map((n) => (
                  <label
                    key={n.field}
                    className="mb-1 flex cursor-pointer items-center gap-2 rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-[13px] text-hi hover:bg-elevated"
                  >
                    <input
                      type="radio"
                      name="compose-number"
                      data-testid={`compose-number-${n.field}`}
                      checked={selectedNumberField === n.field}
                      onChange={() => setSelectedNumberField(n.field)}
                    />
                    <span>
                      {n.label} · {formatPhoneNumber(n.value)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mb-3">
        <p className="mb-1.5 text-[12px] font-medium text-hi">Ticket</p>
        {ticketId ? (
          <div data-testid="compose-ticket-chip" className="flex items-center gap-2">
            <span className="text-[13px] text-hi">#{ticketId}</span>
            <button
              type="button"
              data-testid="compose-ticket-clear"
              aria-label="Clear ticket"
              onClick={() => setTicketId(null)}
              className="text-[13px] text-lo hover:text-hi"
            >
              ×
            </button>
          </div>
        ) : (
          <>
            {showRecentTickets && (
              <div data-testid="compose-recent-tickets" className="mb-2">
                {recentTickets.tickets.map((r) => {
                  const selected = selectedRecentTicketId === r.ticket
                  return (
                    <button
                      key={r.ticket}
                      type="button"
                      data-testid={`compose-recent-ticket-${r.ticket}`}
                      aria-pressed={selected}
                      onClick={() => handleRecentTicketClick(r.ticket)}
                      className={
                        selected
                          ? 'mb-1 block w-full rounded-[8px] border border-brand-primary-strong bg-elevated px-2.5 py-1.5 text-left text-[13px] text-hi'
                          : 'mb-1 block w-full rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-left text-[13px] text-hi hover:bg-elevated'
                      }
                    >
                      {r.ticket}
                      {r.subject ? ` — ${r.subject}` : ''}
                    </button>
                  )
                })}
              </div>
            )}
            {showRecentTicketsUnavailable && (
              <p data-testid="compose-recent-tickets-unavailable" className="mb-1.5 text-[12px] text-lo">
                {COMPOSE_RECENT_TICKETS_UNAVAILABLE_TEXT}
              </p>
            )}
            <input
              data-testid="compose-ticket-input"
              type="text"
              value={ticketInput}
              onChange={(e) => {
                setTicketInput(e.target.value)
                setSelectedRecentTicketId(null)
              }}
              onKeyDown={handleTicketKeyDown}
              placeholder="Ticket number, e.g. 4821 or #4821 (optional)"
              className="h-[34px] w-full rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi"
            />
          </>
        )}
      </div>

      <div className="mb-1">
        <p className="mb-1.5 text-[12px] font-medium text-hi">Message</p>
        <textarea
          data-testid="compose-body-textarea"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder="Write a message…"
          className="w-full resize-none rounded-[8px] border border-subtle bg-card px-2.5 py-1.5 text-[13px] text-hi placeholder:text-lo"
        />
      </div>

      <div className="mt-3 flex justify-end">
        <Button data-testid="compose-send-btn" disabled={!canSend} onClick={() => void handleSend()}>
          Send
        </Button>
      </div>
    </Modal>
  )
}
